import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { api, throwIfNotOk } from '../lib/api';
import { formatUsd } from '../lib/format';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { TextInput } from '@siliconx/shared/ui/Field';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { requireOrgFeature } from '../lib/orgFeatureGate';

// SIL-1108: `orgFeature: 'lots'` (customerNav.ts) is business-only. This
// page's own mount-time fetch (`load()`, below) fired unconditionally, so a
// direct `/lots` hit called `GET /api/client/lots` (403 for an individual
// org) before the root `OrgFeatureGate`'s (__root.tsx) `useEffect`-based
// redirect ever landed — same class of bug as SIL-1102. `/lots` has no
// nested children (`/lots/$lotId` opts out of this route's layout via the
// `lots_.$lotId.tsx` trailing-underscore convention), so the gate lives
// directly on this leaf route's own `beforeLoad` rather than a separate
// layout file.
export const Route = createFileRoute('/lots')({
  beforeLoad: () => requireOrgFeature('lots'),
  component: LotsPage,
});

interface LotRow {
  id: string;
  name: string;
  lotType: 'cluster' | 'lot' | 'rack';
  status: string;
  createdAt: string | null;
  teamName: string | null;
  dataCenterName: string | null;
  internalLocation: string | null;
  memberCount: number;
  fmvMedTotalUsd: number;
  totalPowerDrawWatts: number | null;
  powerDrawReporting: number;
}

/** Watts -> kW, one decimal. Same small local formatter as
 *  routes/lots_.$lotId.tsx — no shared formatter exists yet for this unit
 *  and it's two call sites, so it isn't hoisted to packages/shared. */
function formatKw(watts: number | null): string {
  if (watts == null) return '—';
  return `${(watts / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })} kW`;
}

export const LOT_TYPE_LABELS: Record<string, string> = { cluster: 'Cluster', lot: 'Lot', rack: 'Rack' };

function statusCls(status: string) {
  return cn(
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
    status === 'listed' ? 'bg-brand-100 text-brand-700'
      : status === 'sold' ? 'bg-green-100 text-green-700'
      : status === 'withdrawn' ? 'bg-zinc-100 text-zinc-500'
      : 'bg-amber-100 text-amber-700',
  );
}

const COLUMNS: ColumnDef<LotRow, unknown>[] = [
  {
    id: 'name',
    header: 'Lot',
    cell: (ctx) => <span className="font-medium text-zinc-800">{ctx.row.original.name}</span>,
  },
  {
    id: 'lotType',
    header: 'Type',
    cell: (ctx) => (
      <span className="text-zinc-500">
        {LOT_TYPE_LABELS[ctx.row.original.lotType] ?? ctx.row.original.lotType}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (ctx) => (
      <span className={statusCls(ctx.row.original.status)}>{ctx.row.original.status}</span>
    ),
  },
  {
    id: 'team',
    header: 'Team',
    cell: (ctx) => (
      <span className="text-zinc-500">{ctx.row.original.teamName ?? '—'}</span>
    ),
  },
  {
    id: 'location',
    header: 'Location',
    cell: (ctx) => {
      const { dataCenterName, internalLocation } = ctx.row.original;
      if (!dataCenterName && !internalLocation) return <span className="text-zinc-400">—</span>;
      return (
        <span className="text-zinc-500">
          {dataCenterName ?? '—'}
          {internalLocation && <span className="text-zinc-400"> · {internalLocation}</span>}
        </span>
      );
    },
  },
  {
    id: 'memberCount',
    header: 'Assets',
    cell: (ctx) => <span className="text-zinc-500">{ctx.row.original.memberCount}</span>,
  },
  {
    id: 'totalPowerDrawWatts',
    header: 'Power draw',
    cell: (ctx) => (
      <span className="text-zinc-500">{formatKw(ctx.row.original.totalPowerDrawWatts)}</span>
    ),
  },
  {
    id: 'fmvMedTotalUsd',
    header: 'Aggregate FMV',
    cell: (ctx) => (
      <span className="text-zinc-700">{formatUsd(ctx.row.original.fmvMedTotalUsd)}</span>
    ),
  },
];

function LotsPage() {
  const navigate = useNavigate();
  const [lots, setLots] = useState<LotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'cluster' | 'lot' | 'rack'>('lot');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.api.client.lots
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as LotRow[];
      })
      .then(setLots)
      .catch((e: unknown) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.api.client.lots.$post({ json: { name: newName.trim(), lotType: newType } });
      await throwIfNotOk(res);
      const lot = (await res.json()) as unknown as { id: string };
      setNewName('');
      void navigate({ to: '/lots/$lotId', params: { lotId: lot.id }, search: { tab: 'overview' } });
    } catch (ex: unknown) {
      setError((ex as Error).message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Lots" pinTo="/lots" />
      <p className="text-sm text-zinc-500">Group assets (e.g. a rack) to inspect aggregate FMV and list as a unit.</p>

      <form onSubmit={create} className="flex gap-2">
        <TextInput className="w-64"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New lot name"
        />
        <select
          value={newType}
          onChange={(e) => setNewType(e.target.value as 'cluster' | 'lot' | 'rack')}
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="lot">Lot</option>
          <option value="cluster">Cluster</option>
          <option value="rack">Rack (servers only)</option>
        </select>
        <Button className="px-3" type="submit" disabled={creating || !newName.trim()}>
          Create Lot
        </Button>
      </form>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-zinc-500">Loading lots…</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">Error: {error}</div>
        ) : lots.length === 0 ? (
          <div className="p-6 text-sm text-zinc-400">No lots yet.</div>
        ) : (
          <DataTable
            data={lots}
            columns={COLUMNS}
            onRowClick={(l) => navigate({ to: '/lots/$lotId', params: { lotId: l.id }, search: { tab: 'overview' } })}
            className="rounded-none border-none"
          />
        )}
      </div>
    </div>
  );
}
